import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RiUserLine } from "react-icons/ri";
import { CiServer, CiMoneyCheck1 } from "react-icons/ci";
import { MdLogout } from "react-icons/md";
import { FaXRay, FaChevronDown, FaChevronUp, FaSpinner } from "react-icons/fa";
import { PiReceiptLight } from "react-icons/pi";
import { BsFiles } from "react-icons/bs";
import { GrUpgrade } from "react-icons/gr";
import { PiFlaskLight } from "react-icons/pi";

import useOnClickOutside from "@minohealth/common/hooks/useOnClickOutside";
import ProfileAvatar from "@minohealth/common/components/profile-avatar";
import ThemeToggle from "../chat-sidebar/extras/ThemeToggle";
import { FeatureFlags } from "@minohealth/common/lib/feature-flags";

function isAdmin(user) {
    if (user?.email?.endsWith("@minohealth.org")) return true;
    return false;
}

const PROMO_ADMIN_EMAIL = "minohealthai@gmail.com";

function isPromoAdmin(user) {
    return user?.email?.toLowerCase() === PROMO_ADMIN_EMAIL;
}

const ProfileMenuCustom = ({
    isOpen,
    onClose,
    user,
    profiles,
    selectedProfile,
    switchAccount,
    handleLogout,
    compact = false,
    containerClassName = ""
}) => {
    const menuRef = useRef(null);
    const [showAccountSwitch, setShowAccountSwitch] = useState(false);
    const [switchingId, setSwitchingId] = useState(null);
    useOnClickOutside(menuRef, onClose);

    async function handleSwitchAccount(profileId) {
        setSwitchingId(profileId);
        await switchAccount(profileId);
        setSwitchingId(null);
        setShowAccountSwitch(false);
    }

    if (!isOpen) return null;

    const otherProfiles = profiles?.filter((p) => p.id !== selectedProfile?.id) || [];

    return (
        <div ref={menuRef} className={`${containerClassName} ${compact ? "text-xs" : ""}`}>
            <div className={`relative bg-popover rounded-xl shadow-lg overflow-hidden border border-border w-[240px] ${compact ? 'text-xs' : ''}`}>

                {/* Profile header with account switcher */}
                {(selectedProfile || user) && (
                    <div className={`${compact ? 'p-2' : 'p-2.5'} border-b border-border`}>
                        <div className={`flex items-center gap-2 bg-muted rounded-full px-3 ${compact ? 'h-9' : 'h-10'}`}>
                            <ProfileAvatar
                                name={selectedProfile?.name || selectedProfile?.email || user?.email}
                                size={compact ? 22 : 26}
                                className="rounded-full shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-foreground truncate">
                                    {selectedProfile?.name || selectedProfile?.email || user?.email}
                                </p>
                                <p className="text-[10px] text-muted-foreground truncate">
                                    {selectedProfile?.email || user?.email}
                                </p>
                            </div>
                            {otherProfiles.length > 0 && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowAccountSwitch(s => !s); }}
                                    className="p-1 bg-transparent hover:bg-transparent rounded-full transition-colors shrink-0"
                                >
                                    {showAccountSwitch
                                        ? <FaChevronUp className="w-3 h-3 text-muted-foreground" />
                                        : <FaChevronDown className="w-3 h-3 text-muted-foreground" />
                                    }
                                </button>
                            )}
                        </div>

                        {showAccountSwitch && (
                            <div className="mt-2 rounded-lg border border-border overflow-hidden">
                                {otherProfiles.map((profile) => (
                                    <button
                                        key={profile.id}
                                        onClick={() => handleSwitchAccount(profile.id)}
                                        disabled={switchingId !== null}
                                        className="w-full flex bg-transparent items-center gap-2 px-3 py-2 text-xs text-popover-foreground hover:bg-muted transition-colors disabled:opacity-60"
                                    >
                                        <div className="relative shrink-0">
                                            <ProfileAvatar name={profile.name || profile.email} size={22} className="rounded-full" />
                                            {switchingId === profile.id && (
                                                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                                                    <FaSpinner className="w-3 h-3 my-[-0.5rem] text-white animate-spin" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 text-left">
                                            <p className="text-xs font-medium text-foreground truncate">{profile.name || profile.email}</p>
                                            <p className="text-[10px] text-muted-foreground truncate">{profile.email}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className={`py-1 ${compact ? 'max-h-[50vh]' : ''} overflow-y-auto custom-scrollbar`}>
                    <ThemeToggle compact={compact} />

                    <div className="my-1.5 mx-3 border-t border-border"></div>

                    <Link
                        to="/account/edit"
                        className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                        onClick={onClose}
                    >
                        <RiUserLine className="w-3.5 h-3.5" />
                        <span className="font-medium">Account Settings</span>
                    </Link>
                    <Link
                        to="/account/topup"
                        className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                        onClick={onClose}
                    >
                        <GrUpgrade className="w-3.5 h-3.5" />
                        <span>Upgrade plan</span>
                    </Link>
                    <Link
                        to="/account/statements"
                        className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                        onClick={onClose}
                    >
                        <PiReceiptLight className="w-3.5 h-3.5" />
                        <span className="font-medium">Billing</span>
                    </Link>

                    <div className="my-1.5 mx-3 border-t border-border"></div>

                    {user?.reviewer?.status !== "approved" && (
                        <Link
                            to="/reviewer-registration"
                            className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                            onClick={onClose}
                        >
                            <FaXRay className="w-3.5 h-3.5" />
                            <span className="font-medium">Become a reviewer</span>
                        </Link>
                    )}

                    {isAdmin(user) && (
                        <Link
                            to="/reviewer-applications"
                            className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                            onClick={onClose}
                        >
                            <BsFiles className="w-3.5 h-3.5" />
                            <span className="font-medium">Reviewer applications</span>
                        </Link>
                    )}
                    {isPromoAdmin(user) && FeatureFlags.PROMOCODES && (
                        <Link
                            to="/promo-codes"
                            className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                            onClick={onClose}
                        >
                            <CiMoneyCheck1 className="w-3.5 h-3.5" />
                            <span className="font-medium">Promo codes</span>
                        </Link>
                    )}
                    {isAdmin(user) && (
                        <Link
                            to="/bio-tools"
                            className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                            onClick={onClose}
                        >
                            <PiFlaskLight className="w-3.5 h-3.5" />
                            <span className="font-medium">Bio tools</span>
                        </Link>
                    )}

                    <Link
                        to="/api-request"
                        className={`flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} text-xs text-popover-foreground hover:bg-muted hover:text-foreground transition-colors group`}
                        onClick={onClose}
                    >
                        <CiServer className="w-3.5 h-3.5" />
                        <span className="font-medium">Request API Access</span>
                    </Link>

                    <div className="my-1.5 mx-3 border-t border-border"></div>

                    <button
                        onClick={handleLogout}
                        className={`chat-sidebar-logout w-full flex items-center gap-2.5 px-3 ${compact ? 'py-1.5' : 'py-2'} bg-transparent text-xs text-red-400 hover:bg-muted hover:text-red-300 transition-colors text-left group`}
                    >
                        <MdLogout className="w-3.5 h-3.5" />
                        <span className="font-medium">Log Out</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileMenuCustom;
